import { useEffect, useState, useRef, RefObject } from "react";
import { socket, type SSHReadyData } from "../../socket";
import { SSHError, SSHErrorCode } from "../../types/ssh-errors";
import { Terminal } from "@xterm/xterm";

interface UseTerminalConnectionProps {
  sessionId: string;
  terminalRef: RefObject<Terminal | null>;
  isSessionReady: boolean;
}

export const useTerminalConnection = ({
  sessionId,
  terminalRef,
  isSessionReady,
}: UseTerminalConnectionProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<SSHErrorCode | undefined>(
    undefined,
  );
  const [connectionStartTime, setConnectionStartTime] = useState<Date | null>(
    null,
  );
  const [sessionClosed, setSessionClosed] = useState(false);
  const [isCommandRunning, setIsCommandRunning] = useState(false);
  const isConnectedRef = useRef(false);
  const isCommandRunningRef = useRef(false);
  const outputBufferRef = useRef("");

  useEffect(() => {
    if (!isSessionReady || !terminalRef.current || !sessionId.trim()) return;

    const term = terminalRef.current;

    // Deferred focus/cursor updates; cleared on teardown so they never touch
    // a disposed terminal.
    const focusTimers = new Set<ReturnType<typeof setTimeout>>();
    const scheduleFocus = (callback: () => void, delay: number) => {
      const timer = setTimeout(() => {
        focusTimers.delete(timer);
        callback();
      }, delay);
      focusTimers.add(timer);
    };

    socket.connect();

    const handleConnect = () => {
      socket.emit("ssh:connect", {
        sessionId: sessionId,
        term: "xterm-256color",
        cols: term.cols,
        rows: term.rows,
      });
    };

    // `data` is optional so a tab loaded during a rolling deploy keeps working
    // against a backend that does not send the payload yet.
    const handleReady = (data?: SSHReadyData) => {
      setIsConnected(true);
      isConnectedRef.current = true;
      setIsReconnecting(false);
      setConnectionError(null);
      setErrorCode(undefined);

      if (data?.resumed) {
        // Same shell as before — keep the scrollback and the original
        // connection time; the server replays what was missed.
        return;
      }

      setConnectionStartTime(new Date());
      term.clear();
    };

    const handleData = (data: string) => {
      term.write(data);

      // Buffer output to check for prompts across multiple data chunks
      // SSH data comes in small chunks, prompt might be split across them
      outputBufferRef.current += data;
      if (outputBufferRef.current.length > 2000) {
        outputBufferRef.current = outputBufferRef.current.slice(-1000);
      }

      // Check if command is running and look for completion
      if (isCommandRunningRef.current) {
        // Check for interrupt signals first
        if (
          data.includes("\x03") || // Ctrl+C echo
          data.includes("\x04") || // Ctrl+D echo
          data.includes("\x1a") // Ctrl+Z echo
        ) {
          setIsCommandRunning(false);
          isCommandRunningRef.current = false;
          outputBufferRef.current = "";
        } else {
          // Look for prompt in recent output
          const recentOutput = outputBufferRef.current.slice(-200);

          // Check for prompt patterns
          let promptDetected = false;

          // Primary detection: Bracketed paste mode (very reliable)
          if (data.includes("\x1b[?2004h")) {
            promptDetected = true;
          }

          // Fallback: Check for basic prompt endings
          if (!promptDetected) {
            // Just check for common prompt endings - simple and fast
            promptDetected =
              recentOutput.endsWith("$ ") ||
              recentOutput.endsWith("# ") ||
              recentOutput.endsWith("> ");
          }

          if (promptDetected) {
            setIsCommandRunning(false);
            isCommandRunningRef.current = false;
            outputBufferRef.current = "";

            // Refocus terminal
            scheduleFocus(() => {
              if (!term.hasSelection()) {
                term.focus();
                term.options.cursorBlink = true;
              }
            }, 50);
          }
        }
      }
    };

    const handleError = (error: SSHError) => {
      setConnectionError(error.message);
      setErrorCode(error.code);

      // If session not found, mark it as closed
      if (error.code === SSHErrorCode.SESSION_NOT_FOUND) {
        setSessionClosed(true);
        setIsConnected(false);
        setIsReconnecting(false);
        isConnectedRef.current = false;
      }
    };

    const handleClose = () => {
      setIsConnected(false);
      isConnectedRef.current = false;
      setIsReconnecting(false);
      setConnectionStartTime(null);
      setSessionClosed(true);
    };

    // The websocket dropped. The SSH shell stays alive on the server, so this
    // is a temporary interruption — socket.io reconnects and resumes it.
    const handleDisconnect = () => {
      setIsConnected(false);
      isConnectedRef.current = false;
      setIsReconnecting(true);
    };

    socket.on("connect", handleConnect);
    socket.on("ssh:ready", handleReady);
    socket.on("ssh:data", handleData);
    socket.on("ssh:error", handleError);
    socket.on("ssh:close", handleClose);
    socket.on("disconnect", handleDisconnect);

    const handleTermData = (data: string) => {
      if (isConnectedRef.current) {
        socket.emit("ssh:data", data);

        // If user sends interrupt signal (Ctrl+C)
        if (data === "\x03") {
          // Reset command running state
          setIsCommandRunning(false);
          isCommandRunningRef.current = false;
          outputBufferRef.current = "";
          scheduleFocus(() => {
            term.focus();
            term.options.cursorBlink = true;
          }, 100);
        }
      }
    };

    const handleTermResize = ({
      cols,
      rows,
    }: {
      cols: number;
      rows: number;
    }) => {
      if (isConnectedRef.current) {
        socket.emit("ssh:resize", { cols, rows });
      }
    };

    const dataDisposable = term.onData(handleTermData);
    const resizeDisposable = term.onResize(handleTermResize);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("ssh:ready", handleReady);
      socket.off("ssh:data", handleData);
      socket.off("ssh:error", handleError);
      socket.off("ssh:close", handleClose);
      socket.off("disconnect", handleDisconnect);
      focusTimers.forEach((timer) => {
        clearTimeout(timer);
      });
      socket.emit("ssh:disconnect");
      socket.disconnect();
      dataDisposable.dispose();
      resizeDisposable.dispose();
    };
  }, [isSessionReady, sessionId, terminalRef]);

  // Losing connectivity only interrupts the transport; the SSH shell stays
  // alive on the server and socket.io resumes it once back online. Listened to
  // for the whole lifetime so a stale "connected" state is cleared regardless
  // of the session's readiness.
  useEffect(() => {
    const handleOffline = () => {
      if (!isConnectedRef.current) return;
      setIsConnected(false);
      isConnectedRef.current = false;
      setConnectionError("Keine Internetverbindung");
      setErrorCode(undefined);
    };

    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const sendCommand = (command: string, isLongRunning?: boolean) => {
    if (isConnected) {
      // Special handling for Ctrl+C
      if (command === "\x03") {
        socket.emit("ssh:data", command);
        setIsCommandRunning(false);
        isCommandRunningRef.current = false;
        outputBufferRef.current = "";
      } else {
        // Mark as command running for long-running commands
        if (isLongRunning) {
          setIsCommandRunning(true);
          isCommandRunningRef.current = true;
          outputBufferRef.current = ""; // Clear buffer for new command
        } else {
          // For non-long-running commands, ensure state is clean
          setIsCommandRunning(false);
          isCommandRunningRef.current = false;
        }

        // Commands come with \r, just send as-is
        socket.emit("ssh:data", command);
      }

      // Refocus terminal after command execution
      if (terminalRef.current) {
        setTimeout(() => {
          terminalRef.current?.focus();
          // Ensure cursor is blinking after refocus
          if (terminalRef.current?.options) {
            terminalRef.current.options.cursorBlink = true;
          }
        }, 50);
      }
    }
  };

  return {
    isConnected,
    isReconnecting,
    connectionError,
    errorCode,
    sendCommand,
    isCommandRunning,
    connectionStartTime,
    sessionClosed,
  };
};
